import { FC, useState } from 'react';
import { H1 } from '../../common/AddEditFormLayout/AddEditFormLayout.styles';
import { InputWrapperLarge } from '../../common/FormElementWrappers/FormElementWrappers.styles';
import { H2 } from '../../common/Headings/Headings.styles';
import { useSusGuiImagesDeleteFile, useSusGuiImagesUploadFile } from '../../services/image/image.service';
import Button from '../Button/Button';
import { Variant } from '../Button/Button.types';
import DropZoneComponent from '../DragAndDropFiles/DropZoneComponent';
import { FileWithPreview } from '../DragAndDropFiles/DropZoneComponent.types';
import ImageResource, { padResourceNumbers } from '../ImageResource/ImageResource';
import { RefetchImageHandle } from '../ImageResource/ImageResource.types';
import Input from '../Input/Input';
import Modal from '../Modal/Modal';
import { showToast } from '../Toast/Toast';
import { ToastTypes } from '../Toast/Toast.types';
import { RowWrapper, StyledButtons } from './ImageForm.styles';
import { ImageFormProps } from './ImageForm.types';
import DeleteConfirmationModal from '../../common/DeleteConfirmationModal/DeleteConfirmationModal';
import { categoryValues } from '../../services/image/image.types';

const ImageForm: FC<ImageFormProps> = ({ resourceNo, attribute }) => {
  const [image, setImage] = useState<FileWithPreview | undefined>();
  const [imageRef, setImageRef] = useState<RefetchImageHandle | null>(null);
  const [hasImageLoaded, setHasImageLoaded] = useState<boolean>(false);

  const [openUploadImageModal, setOpenUploadImageModal] = useState(false);
  const { mutate: uploadImage } = useSusGuiImagesUploadFile(attribute, padResourceNumbers(resourceNo, 3, 'jpg'));
  const closeUploadImageModal = () => {
    setOpenUploadImageModal(false);
    setImage(undefined);
  };

  const [openRemoveImageModal, setOpenRemoveImageModal] = useState(false);
  const { mutate: deleteImage } = useSusGuiImagesDeleteFile(attribute, padResourceNumbers(resourceNo, 3, 'jpg'));
  const closeDeleteImageModal = () => {
    setOpenRemoveImageModal(false);
  }

  const submitImage = () => {
    const formData = new FormData();
    if (image) {
      const blob = new Blob([image], { type: 'image/jpeg' });
      formData.append('file', blob);
      uploadImage(formData, {
        onSuccess: () => {
          imageRef?.refetchImage();
          closeUploadImageModal();
          showToast({
            label: 'GUI Image Successfully Updated.',
            id: 'guiImageSuccess',
            toastType: ToastTypes.Success,
          });
        },
      });
    }
  };

  const onDelete = () => {
    deleteImage(undefined, {
      onSuccess: () => {
        imageRef?.refetchImage();
        closeDeleteImageModal();
        setHasImageLoaded(false);
        showToast({
          label: 'GUI Image Successfully Removed.',
          id: 'guiImageRemovalSuccess',
          toastType: ToastTypes.Success,
        });
      },
    });
  };

  return (
    <>
      <H1>GUI Image</H1>
      <RowWrapper>
        <InputWrapperLarge>
          <Input label='File Name' id='fileName' value={padResourceNumbers(resourceNo, 3, 'jpg')} disabled />
        </InputWrapperLarge>
        <ImageResource
          ref={setImageRef}
          resourceNo={resourceNo}
          alt={`${categoryValues[attribute]} Image not loaded`}
          isLeftPadded={true}
          leftPadAmount={3}
          category={attribute}
          setHasImageLoaded={setHasImageLoaded}
        />
      </RowWrapper>
      <RowWrapper>
        <Button
          label='Upload New'
          onClick={() => setOpenUploadImageModal(true)}
          variant={Variant.Primary}
          disabled={false}
          isLoading={false}
          testId='UploadNewImageModalButton'
        />
        <Button
            label='Delete Image'
            onClick={() => setOpenRemoveImageModal(true)}
            variant={Variant.Secondary}
            disabled={!hasImageLoaded}
            isLoading={false}
            testId={'DeleteImageModalButton'}
        />
      </RowWrapper>
      <Modal id='addGuiImage' title='Assign Image' open={openUploadImageModal} onClose={closeUploadImageModal}>
        <div>
          <H2 style={{ paddingBottom: '10px' }}>Upload New</H2>
          <DropZoneComponent testId='imageDropZone' setImage={setImage} image={image} />
          <StyledButtons>
            <Button label='Cancel' onClick={closeUploadImageModal} variant={Variant.Secondary} />
            <Button testId='SaveImageModalButton' label='Save' disabled={!image} onClick={submitImage} />
          </StyledButtons>
        </div>
      </Modal>
      <DeleteConfirmationModal
          title={'Are you sure you want to delete this image?'}
          onDelete={onDelete}
          onClose={closeDeleteImageModal}
          open={openRemoveImageModal}
      />
    </>
  );
};

export default ImageForm;
